import { Fragment, memo, useMemo } from 'react';
import {
  Grid,
  Divider,
  useEventCallback,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormikErrors, FormikTouched } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import ListCategory from 'practice/components/shared/list-category';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import AddressAutoComplete from 'system/components/shared/search-address';
import StreetNumberList, { StreetNumberVM } from 'system/components/shared/street-number-list';
import { DatePicker } from '@mui/lab';
import ListHouseOccupation from 'practice/components/shared/list-house-occupation';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';
import ListaOccupantiLegittimi from './lista-occupanti-legittimi';
import AutocompleteListaSedi from './autocomplete-sedi';
import AutocompleteListaSerie from './autocomplete-serie';
import ListaEntiERP from './lista-enti-erp';
// import CadastralAddressTable from './cadastral-address-table';
import DatiProprietario, { TipoProprietario } from './dati-proprietario';
import { HouseOccupationType, TipoOccupazioneLegittima } from 'practice/constants/house-occupation';

interface FormProps {
  values?: ItalianVM;
  onChange: (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => void;
  onChanges: (values: ItalianVM) => void;
  errors?: FormikErrors<ItalianVM>;
  touched?: FormikTouched<ItalianVM>;
  disabled?: boolean;
  isCambioResidenza?: boolean;
  isImmigrazione?: boolean;
  intestatarioIsAnpr?: boolean;
  isEmigr?: boolean;
  typeResidence?: string;
}

const ItalianFormComponent = ({
  values,
  onChange,
  onChanges,
  errors,
  touched,
  disabled,
  isCambioResidenza,
  isImmigrazione,
  intestatarioIsAnpr,
  isEmigr,
  typeResidence,
}: FormProps): React.ReactElement => {
  const onChangeCategory = useEventCallback((cat: SelectChangeEvent<string>) => {
    onChange('cat', cat.target.value);
  });

  const onChangeMunicipalityList = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipality', municipality);
  });

  const onChangeMunicipalityStructureList = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('municipalityResidenceStruttura', municipality);
    }
  );

  const onChangeStreetNumberList = useEventCallback((item: StreetNumberVM | null) => {
    const enteErp =
      item?.tipoAbitazione === 'A' ? 'ALER' : item?.tipoAbitazione === 'C' ? 'MM' : undefined;
    onChanges({
      number: item?.id ? Number.parseInt(item.id?.toString(), 10) : undefined,
      cap: item?.cap || '',
      exponent: item?.exponent,
      tipoAbitazione: item?.tipoAbitazione,
      enteERP:
        item?.tipoAbitazione === 'A' ? 'ALER' : item?.tipoAbitazione === 'C' ? 'MM' : undefined,
      pecEnte: enteErp ? (enteErp === 'MM' ? 'info@pec.mmspa.eu' : 'protogen@pec.aler.mi.it') : '',
    });
  });

  const onChangeHouseOccupation = useEventCallback((value: string) => {
    const isErp = value === HouseOccupationType.IntestatarioContrattoImmobileResidenzialePubblica;
    onChanges({
      houseOccupationType: value,
      sub: '',
      section: '',
      particle: '',
      sheet: '',
      note: '',
      cat: '',
      numberContract: '',
      dateContract: null,
      officeContract: null,
      occupantiLegittimi: '',
      assensoOspitante: undefined,
      // Proprietario
      tipoProprietario: TipoProprietario.PersonaFisica,
      dataNascitaProprietario: null,
      fiscalCodeProprietario: '',
      municipalityResidenceProprietario: '',
      nameProprietario: '',
      surnameProprietario: '',
      addressProprietario: '',
      effettuatoControlloSiatel: false,
      // // Occupante
      // fiscalCodeOccupante: '',
      // surnameOccupante: '',
      // nameOccupante: '',
      // addressOccupante: '',
      // municipalityResidenceOccupante: null,
      // Ospitante
      fiscalCodeOspitante: '',
      surnameOspitante: '',
      nameOspitante: '',
      municipalityResidenceOspitante: null,
      municipalityResidenceOspitanteERP: '',
      addressOspitante: '',
      // Struttura
      pecEnteStruttura: '',
      fiscalCodeStruttura: '',
      nameStruttura: '',
      addressStruttura: '',
      municipalityResidenceStruttura: null,
      ...(isErp ? {} : { enteERP: '', pecEnte: '' }),
    });
  });

  const isRequiredCadastral = useMemo(() => {
    if (
      values?.section?.toUpperCase() === 'NC' ||
      values?.section?.toUpperCase() === 'NP' ||
      (values?.houseOccupationType !== HouseOccupationType.Proprietario &&
        values?.houseOccupationType !== HouseOccupationType.Usufruttuario)
    ) {
      return false;
    }
    return true;
  }, [values?.section, values?.houseOccupationType]);

  const isRequiredControlRevenueAgency = useMemo(() => {
    if (
      values?.houseOccupationType !== HouseOccupationType.IntestatarioContrattoAgenziaEntrate &&
      values?.houseOccupationType !== HouseOccupationType.ComodatarioAgenziaEntrate
    ) {
      return false;
    }
    return true;
  }, [values?.houseOccupationType]);

  const isEmptyDatiCatastali = useMemo(() => {
    if (!disabled) {
      return false;
    }
    return !Boolean(
      values?.section ||
        values?.sheet ||
        values?.particle ||
        values?.sub ||
        values?.cat ||
        values?.isHomeless
    );
  }, [values, disabled]);

  const isASSDatiCatastali = useMemo(() => {
    if (!disabled) {
      return false;
    }
    return Boolean(
      values?.sheet === 'ASS' || values?.particle === 'ASSENTE' || values?.sub === 'ASS'
    );
  }, [values, disabled]);

  const isEmptyDatiAe = useMemo(() => {
    if (!disabled) {
      return false;
    }
    return !Boolean(values?.officeContract || values?.numberContract || values?.dateContract);
  }, [values, disabled]);

  const selectedAddress = useMemo(() => {
    if (values?.addressAuto !== undefined) {
      return values.addressAuto;
    }

    if (values?.address) {
      return {
        ...values.address,
        description: `${values.descrizioneSpecieToponimo} ${values.address.description}`,
      };
    }

    return null;
  }, [values]);

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <PmiTitleDivider>Indirizzo</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={8}>
        <MunicipalityList
          label="Comune"
          required
          value={values?.municipality || null}
          disabled={disabled || (isCambioResidenza && !intestatarioIsAnpr)}
          onChange={(municipality) => onChangeMunicipalityList(municipality)}
          id="municipality"
          error={touched?.municipality && Boolean(errors?.municipality)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label={!isEmigr ? 'C.A.P. *' : 'C.A.P.'}
          aria-label="cap"
          id="cap"
          value={values?.cap || ''}
          onChange={(event) => {
            onChange('cap', event.target.value);
          }}
          fullWidth
          error={touched?.cap && Boolean(errors?.cap)}
          helperText={errors?.cap}
          disabled={!isEmigr || disabled}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        {isEmigr ? (
          <PmiTextField
            label={!isEmigr ? 'Indirizzo *' : 'Indirizzo'}
            aria-label="indirizzo"
            id="address"
            value={values?.addressAuto?.description || ''}
            onChange={(event) => {
              onChange('addressAuto', {
                description: event.target.value,
              });
            }}
            fullWidth
            disabled={disabled}
            error={touched?.addressAuto && Boolean(errors?.addressAuto)}
            helperText={errors?.addressAuto}
          />
        ) : (
          <AddressAutoComplete
            required={!isEmigr}
            selectedValue={selectedAddress}
            handleChange={(value) => {
              onChange('addressAuto', value);
            }}
            disabled={disabled}
            error={touched?.addressAuto && Boolean(errors?.addressAuto)}
          />
        )}
      </Grid>
      <Grid item xs={12} md={3}>
        {isEmigr ? (
          <PmiTextField
            label={!isEmigr ? 'Numero *' : 'Numero'}
            aria-label="numero"
            id="number"
            value={values?.number || ''}
            onChange={(event) => {
              onChange('number', event.target.value);
            }}
            disabled={disabled}
            fullWidth
            error={touched?.number && Boolean(errors?.number)}
            helperText={errors?.number}
          />
        ) : (
          <StreetNumberList
            label={!isEmigr ? 'Numero *' : 'Numero'}
            onChange={onChangeStreetNumberList}
            noEnded
            valueStreetNumber={
              values?.number
                ? {
                    id: values?.number,
                    value: `${values?.number?.toString()}${transformExponent(
                      values?.exponent || ''
                    )}`,
                  }
                : null
            }
            showExponent
            disabled={disabled || !values?.addressAuto?.code}
            circulationArea={values?.addressAuto?.code?.toString() || ''}
            error={touched?.number && Boolean(errors?.number)}
            helperText={errors?.number}
          />
        )}
      </Grid>
      <Fragment>
        <Grid item xs={12} md={5}>
          <FormControlLabel
            control={
              <Checkbox
                id="assensoAggregazione"
                checked={values?.assensoAggregazione || false}
                onChange={() => onChange('assensoAggregazione', !values?.assensoAggregazione)}
                inputProps={{ 'aria-label': 'Assenso Aggregazione' }}
                disabled={disabled}
              />
            }
            label="Assenso Aggregazione"
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <FormControlLabel
            control={
              <Checkbox
                id="aggregazioneLegittima"
                checked={values?.aggregazioneLegittima || false}
                onChange={() => onChange('aggregazioneLegittima', !values?.aggregazioneLegittima)}
                inputProps={{ 'aria-label': 'Aggregazione Legittima' }}
                disabled={disabled}
              />
            }
            label="Aggregazione Legittima"
          />
        </Grid>
      </Fragment>
      {/* <Grid item xs={12}>
        <CadastralAddressTable
          codiceEnte={params.codiceEnte}
          idAnnoPratica={params.idAnnoPratica}
          typeRegister={params.tipoRegistro}
          progressivoPratica={params.progressivoPratica}
        />
      </Grid> */}
      <Grid item xs={12} md={8}>
        <ListHouseOccupation
          error={touched?.houseOccupationType && Boolean(errors?.houseOccupationType)}
          value={values?.houseOccupationType || ''}
          onChange={onChangeHouseOccupation}
          disabled={disabled}
        />
      </Grid>
      {isImmigrazione || isCambioResidenza ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isHomeless"
                checked={values?.isHomeless || false}
                onChange={() =>
                  onChanges({ isHomeless: !values?.isHomeless, houseOccupationType: '' })
                }
                inputProps={{ 'aria-label': 'Senza fissa dimora' }}
                disabled={disabled}
              />
            }
            label="Senza fissa dimora"
          />
        </Grid>
      ) : undefined}
      {values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo ? (
        <>
          <Grid item xs={12} md={8}>
            <ListaOccupantiLegittimi
              typeResidence={typeResidence}
              error={touched?.occupantiLegittimi && Boolean(errors?.occupantiLegittimi)}
              value={values?.occupantiLegittimi || ''}
              onChange={(value) => {
                const isErp =
                  value === TipoOccupazioneLegittima.AutorizzatoERP ||
                  value === TipoOccupazioneLegittima.OspiteIntestatarioERP;

                const hasAssensoOspitante =
                  value === TipoOccupazioneLegittima.OspiteProprietario ||
                  value ===
                    TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario ||
                  value === TipoOccupazioneLegittima.OspiteIntestatarioERP ||
                  value === TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate;
                onChanges({
                  occupantiLegittimi: value,
                  sub: '',
                  section: '',
                  particle: '',
                  sheet: '',
                  note: '',
                  cat: '',
                  numberContract: '',
                  dateContract: null,
                  officeContract: null,
                  assensoOspitante: hasAssensoOspitante ? true : undefined,
                  // Proprietario
                  fiscalCodeProprietario: '',
                  municipalityResidenceProprietario: '',
                  nameProprietario: '',
                  surnameProprietario: '',
                  addressProprietario: '',
                  effettuatoControlloSiatel: false,
                  // Ospitante
                  fiscalCodeOspitante: '',
                  surnameOspitante: '',
                  nameOspitante: '',
                  municipalityResidenceOspitante: null,
                  municipalityResidenceOspitanteERP: '',
                  addressOspitante: '',
                  // Struttura
                  pecEnteStruttura: '',
                  fiscalCodeStruttura: '',
                  nameStruttura: '',
                  addressStruttura: '',
                  municipalityResidenceStruttura: null,
                  ...(isErp ? {} : { enteERP: '', pecEnte: '' }),
                });
              }}
              disabled={disabled}
            />
          </Grid>
          {values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteProprietario ||
          values?.occupantiLegittimi ===
            TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario ||
          values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteIntestatarioERP ||
          values?.occupantiLegittimi ===
            TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate ? (
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="assensoOspitante"
                    checked={values?.assensoOspitante || false}
                    onChange={() => onChange('assensoOspitante', !values?.assensoOspitante)}
                    inputProps={{ 'aria-label': 'Assenso Ospitante' }}
                    disabled={disabled}
                  />
                }
                label="Assenso Ospitante"
              />
            </Grid>
          ) : undefined}
        </>
      ) : undefined}
      {values?.houseOccupationType === HouseOccupationType.IntestatarioContrattoAgenziaEntrate ||
      values?.houseOccupationType === HouseOccupationType.ComodatarioAgenziaEntrate ||
      (values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
        values.occupantiLegittimi ===
          TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario) ? (
        <>
          {isEmptyDatiAe ? undefined : (
            <>
              <Grid item xs={12}>
                <PmiTitleDivider>
                  Dati contratto{' '}
                  {values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteIntestatarioERP
                    ? ''
                    : 'Agenzia Entrate'}
                </PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={3}>
                <AutocompleteListaSedi
                  label="Sede"
                  aria-label="Sede"
                  required={!isRequiredControlRevenueAgency}
                  disabled={disabled}
                  error={touched?.officeContract && Boolean(errors?.officeContract)}
                  value={values?.officeContract || null}
                  onChange={(value) => onChanges({ officeContract: value, serie: null })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  id="numberContract"
                  label={!isRequiredControlRevenueAgency ? 'Numero' : 'Numero *'}
                  aria-label="Numero"
                  fullWidth
                  disabled={disabled}
                  helperText={errors?.numberContract}
                  error={touched?.numberContract && Boolean(errors?.numberContract)}
                  value={values?.numberContract || ''}
                  onChange={(event) => onChange('numberContract', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label={!isRequiredControlRevenueAgency ? 'Data' : 'Data *'}
                  aria-label="Data"
                  value={values?.dateContract || null}
                  disabled={disabled}
                  onChange={(value) => onChange('dateContract', value)}
                  renderInput={(pickerParams) => (
                    <PmiTextField
                      {...pickerParams}
                      id="dateContract"
                      helperText={errors?.dateContract}
                      error={touched?.dateContract && Boolean(errors?.dateContract)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <AutocompleteListaSerie
                  required={!isRequiredControlRevenueAgency}
                  label="Serie"
                  aria-label="Serie"
                  codUfficio={values.officeContract?.id?.toString()}
                  disabled={!values.officeContract || disabled}
                  error={touched?.serie && Boolean(errors?.serie)}
                  value={values?.serie || null}
                  onChange={(value) => onChange('serie', value)}
                />
              </Grid>
            </>
          )}
        </>
      ) : undefined}
      {values?.houseOccupationType &&
      values.occupantiLegittimi !== TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate &&
      !isEmigr ? (
        <>
          {isASSDatiCatastali || !isEmptyDatiCatastali ? (
            <>
              <Grid item xs={12}>
                <PmiTitleDivider>Dati catastali</PmiTitleDivider>
              </Grid>
              {values.occupantiLegittimi ===
              TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario ? (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="effettuatoControllo"
                        checked={values?.effettuatoControlloSiatel || false}
                        onChange={() =>
                          onChange('effettuatoControlloSiatel', !values?.effettuatoControlloSiatel)
                        }
                        inputProps={{ 'aria-label': 'Effettuato controllo SIATEL' }}
                        disabled={disabled}
                      />
                    }
                    label="Effettuato controllo SIATEL"
                  />
                </Grid>
              ) : undefined}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  id="section"
                  label="Sezione"
                  aria-label="Sezione"
                  fullWidth
                  disabled={disabled}
                  helperText={errors?.section}
                  error={touched?.section && Boolean(errors?.section)}
                  value={values?.section?.toUpperCase() || ''}
                  onChange={(event) => onChange('section', event.target.value?.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  id="sheet"
                  label={!isRequiredCadastral ? 'Foglio' : 'Foglio *'}
                  aria-label="Foglio"
                  fullWidth
                  type="number"
                  disabled={disabled}
                  helperText={errors?.sheet}
                  error={touched?.sheet && Boolean(errors?.sheet)}
                  value={values?.sheet || ''}
                  onChange={(event) => onChange('sheet', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  id="particle"
                  label={!isRequiredCadastral ? 'Particella' : 'Particella *'}
                  aria-label="Particella"
                  fullWidth
                  type="number"
                  disabled={disabled}
                  helperText={errors?.particle}
                  error={touched?.particle && Boolean(errors?.particle)}
                  value={values?.particle || ''}
                  onChange={(event) => onChange('particle', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  id="sub"
                  label={!isRequiredCadastral ? 'Subalterno' : 'Subalterno *'}
                  aria-label="Subalterno"
                  fullWidth
                  type="number"
                  disabled={disabled}
                  helperText={errors?.sub}
                  error={touched?.sub && Boolean(errors?.sub)}
                  value={values?.sub || ''}
                  onChange={(event) => onChange('sub', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ListCategory
                  disabled={disabled}
                  value={values?.cat || ''}
                  error={touched?.cat && Boolean(errors?.cat)}
                  onChange={onChangeCategory}
                />
              </Grid>
            </>
          ) : undefined}
        </>
      ) : undefined}
      {values?.houseOccupationType !== HouseOccupationType.Proprietario &&
      values?.houseOccupationType !== HouseOccupationType.IntestatarioContrattoAgenziaEntrate &&
      values?.houseOccupationType !== HouseOccupationType.ComodatarioAgenziaEntrate ? (
        <>
          {values?.houseOccupationType ===
            HouseOccupationType.IntestatarioContrattoImmobileResidenzialePubblica ||
          values?.occupantiLegittimi === TipoOccupazioneLegittima.AutorizzatoERP ||
          values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteIntestatarioERP ? (
            <>
              <Grid item xs={12} md={6}>
                <ListaEntiERP
                  disabled={disabled}
                  error={touched?.enteERP && Boolean(errors?.enteERP)}
                  value={values?.enteERP || ''}
                  onChange={(value) =>
                    onChanges({
                      enteERP: value,
                      pecEnte: value
                        ? value === 'MM'
                          ? 'info@pec.mmspa.eu'
                          : 'protogen@pec.aler.mi.it'
                        : '',
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="pec-ente"
                  label="PEC Ente"
                  aria-label="PEC Ente"
                  fullWidth
                  disabled
                  helperText={errors?.pecEnte}
                  error={touched?.pecEnte && Boolean(errors?.pecEnte)}
                  value={values?.pecEnte || ''}
                  onChange={(event) => onChange('pecEnte', event.target.value)}
                />
              </Grid>
            </>
          ) : undefined}
        </>
      ) : undefined}
      {values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
      (values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteIntestatarioERP ||
        values?.occupantiLegittimi ===
          TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario) ? (
        <>
          {disabled &&
          !values.fiscalCodeOspitante &&
          !values.surnameOspitante &&
          !values.nameOspitante &&
          !values.municipalityResidenceOspitante &&
          !values.addressOspitante ? undefined : (
            <>
              <Grid item xs={12}>
                <PmiTitleDivider>Dati Ospitante</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="fiscalCodeOspitante"
                  label="Codice Fiscale"
                  disabled={disabled}
                  aria-label="Codice Fiscale"
                  fullWidth
                  helperText={errors?.fiscalCodeOspitante}
                  error={touched?.fiscalCodeOspitante && Boolean(errors?.fiscalCodeOspitante)}
                  value={values?.fiscalCodeOspitante || ''}
                  onChange={(event) => onChange('fiscalCodeOspitante', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="surnameOspitante"
                  label="Cognome"
                  disabled={disabled}
                  aria-label="Cognome"
                  fullWidth
                  helperText={errors?.surnameOspitante}
                  error={touched?.surnameOspitante && Boolean(errors?.surnameOspitante)}
                  value={values?.surnameOspitante || ''}
                  onChange={(event) => onChange('surnameOspitante', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="nameOspitante"
                  label="Nome"
                  disabled={disabled}
                  aria-label="Nome"
                  fullWidth
                  helperText={errors?.nameOspitante}
                  error={touched?.nameOspitante && Boolean(errors?.nameOspitante)}
                  value={values?.nameOspitante || ''}
                  onChange={(event) => onChange('nameOspitante', event.target.value)}
                />
              </Grid>
              {values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteIntestatarioERP ? (
                <>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Data di nascita"
                      aria-label="Data di nascita"
                      value={values?.dataNascitaOspitante || null}
                      disabled={disabled}
                      onChange={(value) => onChange('dataNascitaOspitante', value)}
                      renderInput={(pickerParams) => (
                        <PmiTextField
                          {...pickerParams}
                          id="dataNascitaOspitante"
                          helperText={errors?.dataNascitaOspitante}
                          error={
                            touched?.dataNascitaOspitante && Boolean(errors?.dataNascitaOspitante)
                          }
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PmiTextField
                      id="municipalityResidenceOspitante"
                      label="Comune Residenza"
                      disabled={disabled}
                      aria-label="Comune Residenza"
                      fullWidth
                      error={
                        touched?.municipalityResidenceOspitanteERP &&
                        Boolean(errors?.municipalityResidenceOspitanteERP)
                      }
                      value={values?.municipalityResidenceOspitanteERP || ''}
                      onChange={(event) =>
                        onChange('municipalityResidenceOspitanteERP', event.target.value)
                      }
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={6}>
                  <MunicipalityList
                    id="municipalityResidenceOspitante"
                    label="Comune Residenza"
                    disabled={disabled}
                    aria-label="Comune Residenza"
                    error={
                      touched?.municipalityResidenceOspitante &&
                      Boolean(errors?.municipalityResidenceOspitante)
                    }
                    value={values?.municipalityResidenceOspitante || null}
                    onChange={(item) => onChange('municipalityResidenceOspitante', item)}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="addressOspitante"
                  label="Indirizzo"
                  aria-label="Indirizzo"
                  disabled={disabled}
                  fullWidth
                  helperText={errors?.addressOspitante}
                  error={touched?.addressOspitante && Boolean(errors?.addressOspitante)}
                  value={values?.addressOspitante || ''}
                  onChange={(event) => onChange('addressOspitante', event.target.value)}
                />
              </Grid>
            </>
          )}
        </>
      ) : undefined}
      {values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
      values?.occupantiLegittimi ===
        TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate ? (
        <>
          {disabled &&
          !values.fiscalCodeStruttura &&
          !values.nameStruttura &&
          !values.municipalityResidenceStruttura &&
          !values.addressStruttura ? undefined : (
            <>
              <Grid item xs={12}>
                <PmiTitleDivider>Dati Struttura</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="fiscalCodeStruttura"
                  label="Codice Fiscale *"
                  disabled={disabled}
                  aria-label="Codice Fiscale"
                  fullWidth
                  helperText={errors?.fiscalCodeStruttura}
                  error={touched?.fiscalCodeStruttura && Boolean(errors?.fiscalCodeStruttura)}
                  value={values?.fiscalCodeStruttura || ''}
                  onChange={(event) => onChange('fiscalCodeStruttura', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="nameStruttura"
                  label="Denominazione Struttura *"
                  aria-label="Denominazione Struttura"
                  disabled={disabled}
                  fullWidth
                  helperText={errors?.nameStruttura}
                  error={touched?.nameStruttura && Boolean(errors?.nameStruttura)}
                  value={values?.nameStruttura || ''}
                  onChange={(event) => onChange('nameStruttura', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MunicipalityList
                  id="municipalityResidenceStruttura"
                  label="Comune Sede struttura"
                  disabled={disabled}
                  required
                  aria-label="Comune Sede struttura"
                  value={values?.municipalityResidenceStruttura || null}
                  onChange={(municipality) => onChangeMunicipalityStructureList(municipality)}
                  error={
                    touched?.municipalityResidenceStruttura &&
                    Boolean(errors?.municipalityResidenceStruttura)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="addressStruttura"
                  label="Indirizzo *"
                  disabled={disabled}
                  aria-label="Indirizzo *"
                  fullWidth
                  helperText={errors?.addressStruttura}
                  error={touched?.addressStruttura && Boolean(errors?.addressStruttura)}
                  value={values?.addressStruttura || ''}
                  onChange={(event) => onChange('addressStruttura', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  id="pec-ente"
                  label="PEC Ente"
                  disabled={disabled}
                  aria-label="PEC Ente"
                  fullWidth
                  helperText={errors?.pecEnteStruttura}
                  error={touched?.pecEnteStruttura && Boolean(errors?.pecEnteStruttura)}
                  value={values?.pecEnteStruttura || ''}
                  onChange={(event) => onChange('pecEnteStruttura', event.target.value)}
                />
              </Grid>
            </>
          )}
        </>
      ) : undefined}
      {(values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
        values?.occupantiLegittimi === TipoOccupazioneLegittima.AffittuarioNonAgenziaEntrate) ||
      (values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
        values?.occupantiLegittimi === TipoOccupazioneLegittima.ComodatariNonAgenziaEntrate) ||
      (values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
        values?.occupantiLegittimi === TipoOccupazioneLegittima.OspiteProprietario) ||
      (values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
        values?.occupantiLegittimi ===
          TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario) ||
      values?.houseOccupationType === HouseOccupationType.IntestatarioContrattoAgenziaEntrate ||
      values?.houseOccupationType === HouseOccupationType.ComodatarioAgenziaEntrate ? (
        <>
          {disabled &&
          !values.fiscalCodeProprietario &&
          !values.surnameProprietario &&
          !values.nameProprietario &&
          !values.municipalityResidenceProprietario &&
          !values.addressProprietario ? undefined : (
            <DatiProprietario
              values={values}
              onChange={onChange}
              onChanges={onChanges}
              errors={errors}
              touched={touched}
              disabled={disabled}
            />
          )}
        </>
      ) : undefined}
      <Grid item container spacing={1}>
        <Grid item xs={12}>
          <PmiTextField
            aria-label="Note"
            label="Note"
            id="note"
            name="note"
            fullWidth
            disabled={disabled}
            type="text"
            variant="outlined"
            multiline
            rows={5}
            value={values?.note || ''}
            onChange={(event) => onChange('note', event.target.value)}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

ItalianFormComponent.displayName = 'Accordion New Residence Form - Italian Form';

const ItalianForm = memo(ItalianFormComponent);

export default ItalianForm;
